import { useAuth } from "../contexts/auth.context";
import { getIdParam } from "../../common/utils/helper";
import { useGetUserQuery, useUpdateProfileMutation } from "../../features/api/userSlice";

import React, { useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    IconButton,
    TextField,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

const Profile: React.FC = () => {
    const id = getIdParam();
    const { user } = useAuth();
    const isOwner = user.id === id;

    const { data: targetUser, isSuccess: loadedTargetUser } = useGetUserQuery(id);
    const [updateProfile, { isLoading: loadingUpdateProfile }] = useUpdateProfileMutation();

    const [editField, setEditField] = useState("");
    const [editValue, setEditValue] = useState("");
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const [dialogError, setDialogError] = useState("");

    const navigate = useNavigate();

    const handleClickOpen = (field: string, value: string) => {
        setEditField(field);
        setEditValue(value);
        setOpen(true);
    };

    const handleClose = () => {
        setDialogError("");
        setOpen(false);
    };

    const handleSave = async () => {
        // Validation
        if (editField === "description" && editValue.length > 500) {
            setDialogError("Description cannot be longer than 500 characters.");
            return;
        }
        if (editField === "education" && editValue.length > 50) {
            setDialogError("Education cannot be longer than 50 characters.");
            return;
        }
        if (editField === "gender" && editValue.length > 10) {
            setDialogError("Gender cannot be longer than 10 characters.");
            return;
        }
        if (editField === "hobby" && editValue.length > 50) {
            setDialogError("Hobby cannot be longer than 50 characters.");
            return;
        }
        if (editField === "age" && (!editValue || isNaN(Number(editValue)) || Number(editValue) <= 0)) {
            setDialogError("Age must be a positive number.");
            return;
        }

        if (!loadingUpdateProfile && loadedTargetUser) {
            try {
                await updateProfile({ id: targetUser.id, [editField]: editValue }).unwrap();
                setError("");
            } catch (error) {
                setError("Failed to update, please try again.");
            }
            setOpen(false);
            setDialogError("");
        }
    };

    if (!loadedTargetUser) {
        return <div></div>;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100vh"
            padding={3}
            sx={{ bgcolor: "#E9FAFF" }}
        >
            <IconButton onClick={() => navigate(-1)} sx={{ position: "absolute", top: 20, left: 20 }}>
                <ArrowBackIcon />
            </IconButton>
            <Card sx={{ width: 400, padding: 2, boxShadow: 3 }}>
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Avatar
                        sx={{ width: 80, height: 80, marginBottom: 2 }}
                        src="https://upload.wikimedia.org/wikipedia/en/thumb/3/33/Patrick_Star.svg/1200px-Patrick_Star.svg.png"
                        alt={targetUser.username}
                    >
                        {targetUser.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="h5" gutterBottom align="center">
                        {targetUser.username}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" align="center">
                        {targetUser.email}
                    </Typography>
                    {error && (
                        <Typography color="error" marginBottom={3}>
                            {error}
                        </Typography>
                    )}
                </Box>
                <CardContent>
                    <Typography variant="h6" gutterBottom align="center">
                        Profile Information
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {[
                            { label: "Description", value: targetUser.description || "" },
                            { label: "Education", value: targetUser.education || "" },
                            { label: "Gender", value: targetUser.gender || "" },
                            { label: "Age", value: !targetUser.age ? "" : String(targetUser.age) },
                            { label: "Hobby", value: targetUser.hobby || "" },
                        ].map((field) => (
                            <Box key={field.label} display="flex" alignItems="center">
                                <Typography variant="subtitle1" color="textSecondary" flexGrow={1}>
                                    {field.label}
                                </Typography>
                                <Typography variant="body2" flexGrow={1} align="center">
                                    {field.value}
                                </Typography>
                                {isOwner && (
                                    <IconButton
                                        onClick={() => handleClickOpen(field.label.toLowerCase(), field.value)}
                                        sx={{ padding: 0 }}
                                    >
                                        <EditIcon fontSize="small" sx={{ opacity: 0.5 }} />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Edit {editField.charAt(0).toUpperCase() + editField.slice(1)}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Please enter your new {editField}.</DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={editField.charAt(0).toUpperCase() + editField.slice(1)}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        error={!!dialogError}
                        helperText={dialogError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Profile;
